import request from '@api/request';
import { ProfileEnum } from '@models/clinic-list';
import type {
  BookingReservePayload,
  GetCalendarManagementPayload,
  GetCalendarManagementResponse,
  GetClinicProfileResponse,
  GetClinicPlaceDoctorsResponse,
  GetUserInsurancesByProfilePayload,
  GetUserInsurancesByProfileResponse,
  GetUserServicesByProfilePayload,
  GetUserServicesByProfileResponse,
  FilterDoctorsBySpecialityAndPlacePayload,
  FilterDoctorsBySpecialityAndPlaceResponse,
  SearchClinicPayload,
  SearchClinicResponse,
} from '@models/clinic-list';

export const getClinicProfile = async (
  profileOwnerId: number,
  ProfileType: ProfileEnum,
): Promise<GetClinicProfileResponse> => {
  return request.get(`/ProfileService/${profileOwnerId}/${ProfileType}`);
};

export const getClinicPlaceDoctors = async (
  profileId: number,
): Promise<GetClinicPlaceDoctorsResponse> => {
  return request.get(`/ProfileService/Place/${profileId}`);
};

export const getUserInsurancesByProfile = async ({
  profileId,
  pageNumber = 1,
  pageSize = 10,
}: GetUserInsurancesByProfilePayload): Promise<GetUserInsurancesByProfileResponse> => {
  return request.get(`/UserInsurance/get-by-profile/${profileId}`, null, {
    headers: {
      PageNumber: String(pageNumber),
      PageSize: String(pageSize),
    },
  });
};

export const getUserServicesByProfile = async ({
  profileId,
  pageNumber = 1,
  pageSize = 10,
}: GetUserServicesByProfilePayload): Promise<GetUserServicesByProfileResponse> => {
  return request.get(`/UserService/get-by-profile/${profileId}`, null, {
    headers: {
      PageNumber: String(pageNumber),
      PageSize: String(pageSize),
    },
  });
};

export const getCalendarManagement = async ({
  id,
  dayOfWeek,
  medicalCenterId,
  serviceId,
  pageNumber,
  pageSize,
  skipCount,
}: GetCalendarManagementPayload): Promise<GetCalendarManagementResponse> => {
  return request.get(`/CalendarManagement/Get/${id}/`, null, {
    params: {
      dayOfWeek,
      MedicalCenterId: medicalCenterId,
      ServiceId: serviceId,
      PageNumber: pageNumber,
      PageSize: pageSize,
      SkipCount: skipCount,
    },
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    },
  });
};

export const reserveBooking = async (
  body: BookingReservePayload,
): Promise<unknown> => {
  return request.post('/Booking/Reserve', body);
};

export const filterDoctorsBySpecialityAndPlace = async ({
  LatestSpecialityId,
  ProfileId,
  ticks = 0,
}: FilterDoctorsBySpecialityAndPlacePayload): Promise<FilterDoctorsBySpecialityAndPlaceResponse> => {
  const response = await request.get(
    '/Doctors/FilterDoctorsBySpecialityAndPlace',
    null,
    {
      params: {
        LatestSpecialityId,
        ProfileId,
        ticks,
      },
    },
  );

  return response?.data?.result ?? [];
};

export const searchClinic = async ({
  search,
  pageNumber = 1,
  pageSize = 20,
}: SearchClinicPayload): Promise<SearchClinicResponse> => {
  const response = await request.get('/Clinic/management/SearchClinic', null, {
    params: {
      Search: search,
      PageNumber: pageNumber,
      PageSize: pageSize,
    },
  });

  if (response?.queryResult) {
    return response;
  }

  if (response?.data?.queryResult) {
    return response.data;
  }

  return {
    queryResult: [],
    pageNumber,
    pageSize,
    totalCount: 0,
  };
};
